/**
 * track:log-jump — full-history posts for the active sidebar tag filter, from
 * GET /api/messages/by-tag (Admin SDK, indexed, live). Replaces the BigQuery
 * call, which lagged live writes and cached a failed fetch as an empty result.
 *
 * Only a page that came back with posts is kept. A failed or empty fetch is
 * never stored as a result, so re-selecting the tag (or Load more) asks the
 * server again.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTagPage } from "../lib/logJump";
import type { TeamLogMessage } from "../types";

interface TagState {
  tag: string | null;
  messages: TeamLogMessage[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

const IDLE: TagState = { tag: null, messages: [], hasMore: false, loading: false, error: null };

export interface UseTagHistory {
  messages: TeamLogMessage[];
  hasMore: boolean;
  loading: boolean;
  error: string | null;
  loadMore: () => void;
}

export function useTagHistory(tag: string | null): UseTagHistory {
  const [state, setState] = useState<TagState>(IDLE);
  const stateRef = useRef(state);
  stateRef.current = state;
  const tagRef = useRef(tag);
  tagRef.current = tag;

  const fetchPage = useCallback(async (forTag: string, beforeSeq?: number) => {
    setState((s) => ({ ...s, tag: forTag, loading: true, error: null }));
    try {
      const page = await fetchTagPage(forTag, beforeSeq);
      if (tagRef.current !== forTag) return;
      setState((s) => ({
        tag: forTag,
        messages: beforeSeq === undefined ? page.messages : [...s.messages, ...page.messages],
        hasMore: page.hasMore,
        loading: false,
        error: null,
      }));
    } catch (err) {
      if (tagRef.current !== forTag) return;
      console.error("[Relay] tag history failed:", err);
      setState((s) => ({ ...s, tag: forTag, loading: false, error: "Couldn't load older posts for this tag." }));
    }
  }, []);

  useEffect(() => {
    try {
      if (!tag) {
        setState(IDLE);
        return;
      }
      setState({ ...IDLE, tag, loading: true });
      void fetchPage(tag);
    } catch (err) {
      console.error("[Relay] tag history effect failed:", err);
    }
  }, [tag, fetchPage]);

  const loadMore = useCallback(() => {
    const s = stateRef.current;
    if (!s.tag || s.loading) return;
    // Nothing loaded yet: only a failed first fetch has anything to retry.
    if (s.messages.length === 0) {
      if (s.error) void fetchPage(s.tag);
      return;
    }
    const oldest = s.messages[s.messages.length - 1].sequence;
    if (oldest === undefined) return;
    void fetchPage(s.tag, oldest);
  }, [fetchPage]);

  const current = state.tag === tag ? state : IDLE;
  return {
    messages: current.messages,
    hasMore: current.hasMore,
    loading: current.loading,
    error: current.error,
    loadMore,
  };
}
